import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { addDays, startOfMonth } from 'date-fns'
import { useUnit } from 'effector-react'
import type { DateRange } from 'react-day-picker'

import { $account } from '@/models/account.ts'
import { $currentCreator } from '@/models/creators.ts'
import { useMaxDateRange } from '@/modules/statistics/onlyfans/hook/useMaxDateRange.ts'
import { dateToString } from '@/utils/date-converter'
import { noop } from '@/utils/misc.ts'

import { getDateInTimezone, getUtcOffsetLabel } from '../ui/calendar/utils'

export enum DateRangeContextKey {
  Default = 'default',
  Social = 'social',
  AutoFollow = 'auto_follow',
  Accounting = 'accounting',
}

export const DateRangeStateContext = createContext<DateRangeProviderStateSet>({
  timezoneLabel: '',
  today: new Date(),
  dates: new Map<DateRangeContextKey, DateRange>(),
  setDateRange: noop,
  maxDateRange: { from: new Date(0), to: new Date() },
})

interface DateRangeProviderProps {
  children: ReactNode
}

interface DateRangeProviderStateSet {
  timezoneLabel: string
  today: Date
  dates: Map<DateRangeContextKey, DateRange>
  setDateRange: (key: DateRangeContextKey, range: DateRange) => void
  maxDateRange: DateRange
}

const createInitialState = (
  today: Date,
): Map<DateRangeContextKey, DateRange> => {
  const map = new Map<DateRangeContextKey, DateRange>()

  map.set(DateRangeContextKey.Default, {
    from: today,
    to: today,
  })
  map.set(DateRangeContextKey.Social, {
    from: addDays(today, -1),
    to: addDays(today, -1),
  })
  map.set(DateRangeContextKey.AutoFollow, {
    from: startOfMonth(today),
    to: today,
  })
  map.set(DateRangeContextKey.Accounting, {
    from: today,
    to: today,
  })

  return map
}

export const DateRangeProvider = ({ children }: DateRangeProviderProps) => {
  const activeCreator = useUnit($currentCreator)
  const account = useUnit($account)
  const today = getDateInTimezone(account?.timezone)
  const timezoneLabel = `(${getUtcOffsetLabel(account?.timezone ?? 'UTC')})`

  const [dateMap, setDateMap] = useState<Map<DateRangeContextKey, DateRange>>(
    () => createInitialState(today),
  )

  const setDateRange = useCallback(
    (key: DateRangeContextKey, range: DateRange) => {
      const { from, to } = range

      setDateMap((prev) => {
        const newMap = new Map<DateRangeContextKey, DateRange>(prev)

        newMap.set(key, {
          from: !from || !to || from.getTime() < to?.getTime() ? from : to,
          to: !from || !to || to.getTime() > from?.getTime() ? to : from,
        })

        return newMap
      })
    },
    [],
  )
  const maxRangeQuery = useMaxDateRange(activeCreator)

  const context = useMemo(
    () => ({
      timezoneLabel,
      today,
      dates: new Map<DateRangeContextKey, DateRange>(dateMap),
      setDateRange,
      maxDateRange: maxRangeQuery.data!,
    }),
    [dateMap, setDateRange, maxRangeQuery.data, today, timezoneLabel],
  )

  return (
    <DateRangeStateContext.Provider value={context}>
      {children}
    </DateRangeStateContext.Provider>
  )
}

interface DateRangeContextProps {
  timezoneLabel: string
  today: Date
  date: DateRange
  setDate: (range: DateRange) => void
  maxDateRange: DateRange
  isOneDay: boolean
}

export const useDateRangeContext = (
  key = DateRangeContextKey.Default,
): DateRangeContextProps => {
  const context = useContext(DateRangeStateContext)

  if (context === undefined) {
    throw new Error('useDateRangeContext must be used within DateRangeProvider')
  }

  const contextDate = context.dates.get(key)
  const date = useMemo(
    () =>
      contextDate ?? {
        from: context.today,
        to: context.today,
      },
    [context.today, contextDate],
  )

  return {
    timezoneLabel: context.timezoneLabel,
    today: context.today,
    date,
    setDate: (range: DateRange) => context.setDateRange(key, range),
    maxDateRange: context.maxDateRange
      ? context.maxDateRange
      : { from: context.today, to: context.today },
    isOneDay:
      !contextDate?.from || !contextDate?.to
        ? false
        : dateToString(new Date(contextDate.from)) ===
          dateToString(new Date(contextDate.to)),
  }
}
